import React, { useEffect, useState, useCallback } from 'react'
import { Grid, GridItem, Box, Icon, Link } from '@chakra-ui/react'
import { FaArrowRight } from 'react-icons/fa'
import { Section, SectionBody, SectionHeader, useAlert } from '@apex/component-library'
import { useFormatMessage } from '../../utils/intlHelpers'
import { RootState } from '../../store'
import { ApexAccounts } from 'apex-ui-services'
import { connect } from 'react-redux'
import AccountSummaryHeader from '../../components/AccountSummaryHeader'
import { SubmittedTransfers } from '../SubmittedTransfers'
import ScheduledTransfers from '../ScheduledTransfers'
import { CashJournalFormikWrapper } from '../../components/CashJournalWrapper/CashJournalWrapper'
import isNil from 'lodash.isnil'
import { TransferSelect, TransferSelectEnum } from '../../components/TransferSelect'
import { AccountDetailsState, AuthorizationState } from '@Types'
import WireTransfers from '../WireTransfers/WireTransfers'
import CheckTransfers from '../CheckTransfers'
import ACHTransfers from '../ACHTransfers'
import { useGetAmountAvailable } from '../../api/useGetAmountAvailable'
import { getViewPermissions, getWritePermissions } from './getPermissions'
import useAccount from '../../hooks/useAccount'
import PositionJournals from '../PositionJournals'
import { useParams } from 'react-router'

export interface Props {
  accountDetails?: AccountDetailsState | null
  getAccountDetails: (account: string) => void
  auth: AuthorizationState
}

export const TransfersPageComponent = ({ accountDetails, getAccountDetails, auth }: Props) => {
  const { accountNumber } = useParams<{ accountNumber: string }>()
  const { setAccountNumber, account } = useAccount()
  const formatMessage = useFormatMessage()
  const viewPermissions = getViewPermissions(auth)
  const writePermissions = getWritePermissions(auth)

  const { setAlert, removeAlert } = useAlert()
  const setAlertMemo = useCallback(setAlert, [])
  const removeAlertMemo = useCallback(removeAlert, [])
  const formatMessageMemo = useCallback(formatMessage, [])
  let errorAlertIds: string[] = []

  setAccountNumber(accountNumber)

  useEffect(() => {
    if (account && account.accountNumber) {
      getAccountDetails(account.accountNumber)
    }
    // eslint-disable-next-line
  }, [account])

  const { amountAvailable, isLoading: amountAvailableLoading, error: amountAvailableError } = useGetAmountAvailable(
    account?.accountNumber || ''
  )

  useEffect(() => {
    if (amountAvailableError || accountDetails?.error) {
      const { id } = setAlertMemo({
        title: formatMessageMemo('common.systemUnavailable'),
        description: formatMessageMemo('common.serviceUnavailable'),
        status: 'error',
        direction: 'row',
        isClosable: true,
        showIcon: true
      })
      errorAlertIds.push(id)
    }
    return () => {
      errorAlertIds.forEach((id) => removeAlertMemo(id))
    }
    // adding errorAlertIds to dependency array causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountAvailableError, accountDetails, setAlertMemo, formatMessageMemo, removeAlertMemo])

  const [selectValue, setSelectValue] = useState<TransferSelectEnum>(TransferSelectEnum.Empty)

  const handleTransferSelectionChange = (value: TransferSelectEnum) => {
    setSelectValue(value as TransferSelectEnum)
  }

  const viewAccountLink = (
    <Link
      href={`${window.location.origin}/accounts/#/search/${account?.accountNumber}`}
      color='white'
      _hover={{ textDecoration: 'none', color: 'white' }}
    >
      View Account History <Icon as={FaArrowRight} color='white' textDecoration='none' />
    </Link>
  )
  return (
    <>
      {account && <AccountSummaryHeader accountData={account} appTitle='CASH TRANSFERS' />}
      <Box padding={4}>
        <Grid templateColumns={{ base: '1fr', xl: '3fr 2fr' }} gap={8}>
          <GridItem>
            <Section>
              <SectionHeader>Transfer Details</SectionHeader>
              <SectionBody>
                <Box marginBottom='1em'>
                  <TransferSelect
                    handleTransferSelectionChange={handleTransferSelectionChange}
                    selectValue={selectValue}
                    permissions={viewPermissions}
                  />
                </Box>
                {selectValue === TransferSelectEnum.ACH && !isNil(amountAvailable) && !amountAvailableLoading && (
                  <ACHTransfers hasSubmitPermission={writePermissions.hasAch} amountAvailable={amountAvailable} />
                )}
                {selectValue === TransferSelectEnum.CashJournal &&
                  !isNil(account) &&
                  !isNil(amountAvailable) &&
                  !amountAvailableLoading && (
                    <CashJournalFormikWrapper
                      account={account}
                      accountDetails={accountDetails}
                      amountAvailable={amountAvailable}
                      hasSubmitPermission={writePermissions.hasCashJournal}
                    />
                  )}
                {selectValue === TransferSelectEnum.Wire &&
                  !isNil(account) &&
                  !isNil(amountAvailable) &&
                  !amountAvailableLoading && (
                    <WireTransfers
                      account={account}
                      accountDetails={accountDetails}
                      amountAvailable={amountAvailable}
                      hasSubmitPermission={writePermissions.hasWire}
                    />
                  )}
                {selectValue === TransferSelectEnum.Check &&
                  !isNil(account) &&
                  !isNil(amountAvailable) &&
                  !amountAvailableLoading && (
                    <CheckTransfers
                      account={account}
                      amountAvailable={amountAvailable}
                      hasSubmitPermission={writePermissions.hasCheck}
                      hasOvernightToApexPerm={writePermissions.hasCheckOvernightToApex}
                    />
                  )}
                {selectValue === TransferSelectEnum.PositionJournal &&
                  !isNil(account) &&
                  !isNil(amountAvailable) &&
                  !amountAvailableLoading && (
                    <PositionJournals hasSubmitPermission={writePermissions.hasPositionJournal} />
                  )}
              </SectionBody>
            </Section>
          </GridItem>
          <GridItem>
            <Section>
              <SectionHeader tools={viewAccountLink}>Recent Transfer Activity</SectionHeader>
              <SectionBody marginTop='3'>
                {!isNil(account?.accountNumber) && (
                  <SubmittedTransfers accountNumber={account?.accountNumber as string} />
                )}
              </SectionBody>
            </Section>
            <Section marginTop='6'>
              <SectionHeader>Transfer Schedules</SectionHeader>
              <SectionBody>
                {!isNil(account?.accountNumber) && <ScheduledTransfers accountNumber={account?.accountNumber} />}
              </SectionBody>
            </Section>
          </GridItem>
        </Grid>
      </Box>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  const legitAuthState = (state as any)?.APEX_LEGIT_AUTHORIZATION
  return {
    accountDetails: ApexAccounts.accounts.selectors.accountDetailsState(state),
    auth: {
      hasBeenAsked: legitAuthState?.status === 'APEX_LEGIT_AUTHORIZATION/LOADING',
      authorization: legitAuthState?.results
    }
  }
}
const mapDispatchToProps = {
  getAccountDetails: ApexAccounts.accounts.actions.getAccount.request
}

export const TransfersPage = connect(mapStateToProps, mapDispatchToProps)(TransfersPageComponent)
